/**
 * /policies/leave/encashment-rules — Session 3c PR 3.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { EncashmentRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function LeaveEncashmentRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-leave-encashment-rules", le],
    queryFn: () => session3cApi.listEncashmentRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<EncashmentRuleItem>[] = [
    {
      headerKey: "policies.leave_encashment.col.leave_type",
      label: "Leave type",
      render: (r) => <span className="font-medium">{r.leave_type_name_en}</span>,
    },
    {
      headerKey: "policies.leave_encashment.col.eos",
      label: "Encashable on EOS",
      render: (r) => (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${r.encashable_on_eos ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
          {r.encashable_on_eos ? "yes" : "no"}
        </span>
      ),
    },
    {
      headerKey: "policies.leave_encashment.col.max_days",
      label: "Max days encashable",
      render: (r) => <span className="font-mono">{r.max_days_encashable.value}</span>,
      traceableValue: (r) => ({ label: t("policies.leave_encashment.col.max_days", "Max days"), stack: r.max_days_encashable }),
    },
    {
      headerKey: "policies.leave_encashment.col.valuation",
      label: "Valuation",
      render: (r) => <span className="text-xs text-text-secondary">{r.valuation_method}</span>,
    },
  ];

  return (
    <PolicyListPage<EncashmentRuleItem>
      title={t("policies.leave_encashment.title", "Leave Encashment Rules")}
      subtitle={t("policies.leave_encashment.subtitle", "What encashes on EOS · cap rules · valuation method (basic vs total wage).")}
      agent="mutabaa"
      domain="leave_encashment"
      newButtonLabel={t("policies.leave_encashment.cta.new", "New Encashment Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.leave_encashment.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.leave_encashment.suggestion.clean_body", "All rules at or above Article 109 floor. Use the popover on Max Days to see the override stack."),
          primaryActionLabel: t("policies.leave_encashment.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "leave-encashment-rules-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.leave_encashment.title", "Leave Encashment Rules"),
        window_label: t("policies.leave_encashment.window", "across active leave types"),
      }}
    />
  );
}
